import * as React from 'react';

import Swiper from 'react-id-swiper';
import 'swiper/css/swiper.css';

import Flex from '@components/layout/flex';

import styled, { css } from 'styled-components';

import SliderArrowIcon from '@components/basics/icons/slider-arrow';
import { Typography } from '@components/basics/typography';

import AppState from '@webapp/store/state';
import { connect } from 'react-redux';

interface SwiperCtrlProps {
  id: string;
  className?: string;
  children: React.ReactElement[];
  centered?: boolean;
  hasOffset?: boolean;
  spaceBetween?: number;
  slidesPerView?: number | string;
  noPagination?: boolean;
  hasArrows?: boolean;
  noNumbers?: boolean;
  arrowsDisabled?: boolean;
  slidesLength?: number;
  isMobile: boolean;
}

const mapsStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
});

const SwiperCtrl: React.FC<SwiperCtrlProps> = ({ slidesLength, ...props }) => {
  const [swiper, setSwiper] = React.useState<any>(null);
  const [activeIndex, setActiveIndex] = React.useState<number>(0);

  const params = {
    spaceBetween: props.spaceBetween ? props.spaceBetween : 10,
    slidePerView: props.slidesPerView ? props.slidesPerView : 1,
    centeredSlides: !!props.centered,
    slidesOffsetBefore: props.hasOffset ? 24 : 0,
    slidesOffsetAfter: props.hasOffset ? 24 : 0,
    grabCursor: true,
    allowTouchMove: true,
    navigation: !!props.hasArrows,
    pagination: !props.noPagination && {
      el: '.slider-pagination',
      clickable: false,
      renderBullet: () => '',
    },
    breakpoints: {
      320: {
        slidesPerView: props.slidesPerView ? props.slidesPerView : 1.125,
      },
      640: {
        slidesPerView: props.slidesPerView ? props.slidesPerView : 1.125,
      },
      768: {
        slidesPerView: props.slidesPerView ? props.slidesPerView : 1.125,
      },
      1024: {
        slidesPerView: props.slidesPerView ? props.slidesPerView : 1.125,
      },
      1330: {
        slidesPerView: props.slidesPerView ? props.slidesPerView : 1.125,
        allowTouchMove: false,
        grabCursor: false,
      },
      2500: {
        spaceBetween: props.spaceBetween ? 100 : 20,
      },
    },
  };

  const initSwiper = (instance: any) => {
    if (instance) {
      instance.on('slideChange', () => setActiveIndex(instance.activeIndex));
      slideTo(0);
    }
    setSwiper(instance);
  };

  const isActive = (index: number) => {
    if (swiper) {
      return activeIndex === index;
    }
  };

  const slideTo = (index: number) => {
    if (swiper) {
      swiper.slideTo(index);
    }
  };

  const slidePrev = () => {
    if (swiper && (slidesLength ? activeIndex - 1 >= 0 : false)) {
      swiper.slidePrev();
    }
  };

  const slideNext = () => {
    if (swiper && (slidesLength ? activeIndex + 1 < slidesLength : false)) {
      swiper.slideNext();
    }
  };

  return (
    <div className={props.className}>
      {!props.isMobile &&
        (props.noNumbers || props.hasArrows ? (
          <Flex
            className="mb-20"
            alignItems="center"
            justifyContent="space-between"
          >
            {props.noNumbers || !slidesLength || slidesLength === 0 ? null : (
              <NumbersWrapper>
                <Flex
                  alignItems="center"
                  justifyContent="flex-start"
                  className="numbers"
                >
                  <Typography>
                    {swiper && String(swiper.activeIndex + 1)}&nbsp;
                  </Typography>
                  <Typography opacity=".25">
                    / {slidesLength || (swiper && swiper.slides.length)}
                  </Typography>
                </Flex>
              </NumbersWrapper>
            )}
            {props.hasArrows && slidesLength && slidesLength > 0 ? (
              <WrapperArrows
                alignItems="center"
                justifyContent="flex-end"
                arrowsDisabled={props.arrowsDisabled}
              >
                <SliderArrowIcon
                  className="arrow"
                  onClick={() => slidePrev()}
                  arrowDirection="left"
                />
                <SliderArrowIcon
                  className="arrow"
                  onClick={() => slideNext()}
                  arrowDirection="right"
                />
              </WrapperArrows>
            ) : null}
          </Flex>
        ) : null)}
      {slidesLength && slidesLength > 0 ? (
        <Swiper {...params} getSwiper={initSwiper}>
          {props.children}
        </Swiper>
      ) : null}
      {props.noPagination || !slidesLength || slidesLength === 0 ? null : (
        <div className="pagination-wrapper">
          <Flex
            alignItems="center"
            justifyContent="center"
            className="pagination"
          >
            {props.children.map((_, index) => {
              return (
                <span
                  className={`bullet ${isActive(index) ? 'active' : ''}`}
                  key={`${props.id}-slider-${index}`}
                  onClick={() => slideTo(index)}
                />
              );
            })}
          </Flex>
        </div>
      )}
    </div>
  );
};

export default connect(mapsStateToProps)(styled(SwiperCtrl)`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  position: relative;
  display: block;

  ${(props) =>
    props.centered
      ? css`
          padding-bottom: 30px;

          ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
            padding-bottom: 50px;
          }

          .swiper-slide {
            &:last-child {
              margin-right: 0 !important;
            }
          }
        `
      : null}

  .pagination-wrapper {
    max-width: 1440px;
    width: 100%;
    box-sizing: border-box;
    padding: 0 30px;
    margin-left: auto;
    margin-right: auto;

    position: absolute;
    bottom: 0;
    left: 0;

    ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
      padding: 0 20px;
      left: 50%;
      transform: translate(-50%, 0);
    }
  }

  .pagination {
    flex-direction: row;
    margin-top: 24px;
    & > * {
      z-index: 4;
      cursor: pointer !important;
    }
  }

  .bullet {
    background-color: ${({ theme }) => theme.colors.primary};
    width: 5px;
    height: 5px;
    border-radius: 50%;
    opacity: 0.4;
    transition: width
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`},
      opacity
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`};

    ${(props) =>
      props.children.length > 2
        ? `
      &:nth-child(even) {
        margin: 0 14px;
      }
    `
        : `
      &:nth-child(2) {
        margin-left: 14px;
      }
    `}

    &.active {
      opacity: 1;
      width: 12px;
      border-radius: 2px;
    }
  }
`);

const WrapperArrows = styled(Flex)<{ arrowsDisabled?: boolean }>`
  .arrow {
    margin-right: 5px;

    &:last-child {
      margin-right: 0;
    }
  }

  ${({ arrowsDisabled }) =>
    arrowsDisabled
      ? css`
          .arrow {
            pointer-events: none;
            cursor: not-allowed;
            opacity: 0.25;
          }
        `
      : null}
`;

const NumbersWrapper = styled.div`
  user-select: none;
`;
